// @flow

import { action, computed, observable } from "mobx";

import Store from "app/stores/Store";
import TimelineItemStore from "app/stores/TimelineItemStore";
import VideoStore from "app/stores/VideoStore";

import UserVideoProgressService from "app/services/UserVideoProgressService";
import VideoStatsService from "app/services/VideoStatsService";

import Dispatcher from "app/util/Dispatcher";
import { states, STATS } from "timelinely-player/TimelinelyPlayer";

const amountOfSecondsToConsiderSeek = 5;
const amountOfSecondsToSaveProgress = 5;

class VideoPlayerStore extends Store {
    @observable played: boolean = false;
    @observable state: string;
    @observable progress: number = 0;
    @observable bufferedProgress: number = 0;
    @observable volume = 100;
    @observable seeking: boolean;
    @observable watchTourEnabled: boolean = false;

    @observable preventAutoPlay: boolean;

    watchProgress: { start?: number, end?: number } = {};
    lastTimeSavedWatchProgress: Date = new Date();

    stateDispatcher: Dispatcher = new Dispatcher();
    seekDispatcher: Dispatcher = new Dispatcher();
    volumeDispatcher: Dispatcher = new Dispatcher();

    @action.bound
    updateState(state: string): void {
        this.state = state;
        this.trackProgress(state);
        this.saveStats(state);
        if (state === states.ENDED)
            this.updateProgress(
                VideoStore.video.duration,
                VideoStore.video.duration
            );
    }

    @action.bound
    play(): void {
        if (TimelineItemStore && TimelineItemStore.isExpanded) {
            TimelineItemStore.unExpand();
        }        
        this.state = states.PLAYING;
        this.stateDispatcher.dispatch(states.PLAYING);
        this.volumeDispatcher.dispatch(this.volume);
        this.played = true;
    }

    @action.bound
    pause(): void {
        this.state = states.PAUSED;
        this.stateDispatcher.dispatch(states.PAUSED);
    }

    @action.bound
    updateProgress(progress: number, bufferedProgressPercentage: number) {
        this.played = true;

        if (TimelineItemStore.wizard) {
            const item = TimelineItemStore.selectedItem;
            
            if (!item) throw Error("selectedItem must not be null/undefined");

            let correctTime = Math.max(Math.min(item.stop_time, progress));

            if (correctTime !== progress) {
                this.pause();
                this.seekTo(correctTime);
                return;
            }
        } else {
            if (this.isSeeking(progress)) {
                this.handleProgressOnSeek(progress);
            }

            this.handleProgressOnInterval();
        }

        const bufferedProgress =
            bufferedProgressPercentage * VideoStore.video.duration;
        if (
            this.progress === progress &&
            this.bufferedProgress === bufferedProgress
        ) {
            // don't change the progress if before play or during pause or it's the video's end
            return;
        }
        this.progress = progress;
        this.bufferedProgress = bufferedProgress;

        TimelineItemStore.progressSelect(progress, this.shouldPreventAutoPlay());
    }

    @action.bound
    setVolume(volume: number) {
        this.volume = volume;
    }

    @action.bound
    replay() {
        TimelineItemStore.deselect();
        this.seekTo(0);
    }

    @action.bound
    seekTo(time: number) {
        this.handleProgressOnSeek(time);
        this.saveStats("seek");
        if (!this.watchTourEnabled) {
            this.seekDispatcher.dispatch(time);
            this.progress = time;
        }
        this.setSeeking(true);
    }
    
    @action.bound
    setSeeking(seeking: boolean) {
        this.seeking = seeking;
        this.state = states.SEEK;
    }

    @action.bound
    setWatchTour(status: boolean) {
        this.watchTourEnabled = status;
    }

    @action.bound
    shouldPreventAutoPlay() {
        if (this.preventAutoPlay) {
            this.preventAutoPlay = false;
            return true;
        }
    }

    @action.bound
    setPreventAutoPlay(prevent: boolean) {
        this.preventAutoPlay = prevent;
    }

    /*
     * Tracking
     */

    trackProgress(state: string) {
        const fn = {
            playing: this.handleProgressOnPlay.bind(this),
            paused: this.handleProgressOnPause.bind(this),
            ended: this.handleProgressOnEnd.bind(this)
        };
        fn[state]();
    }

    handleProgressOnPlay() {
        if (this.watchProgress.start == null) {
            this.watchProgress.start = 0;
        }
    }

    handleProgressOnPause() {
        if (this.watchProgress.start != null) {
            this.watchProgress.end = this.progress;
        }
    }

    handleProgressOnEnd() {
        if (this.watchProgress.start != null) {
            this.watchProgress.end = VideoStore.video.duration;
        }
    }

    handleProgressOnSeek(time: number) {
        if (this.watchProgress.start != null) {
            this.watchProgress.end = this.progress;
            this.saveUserProgress();
        }

        this.watchProgress.start = time;
        this.watchProgress.end = undefined;
    }

    handleProgressOnInterval() {
        if (this.shouldUpdateOnInterval()) {
            this.watchProgress.end = this.progress;
            this.saveUserProgress();
        }
    }

    shouldUpdateOnInterval(): boolean {
        const now = new Date().getTime();
        const lastTime = this.lastTimeSavedWatchProgress.getTime();

        const diff = Math.abs(now - lastTime);

        return (
            this.watchProgress.start != null &&
            diff >= amountOfSecondsToSaveProgress * 1000 &&
            this.state === states.PLAYING
        );
    }

    isSeeking(progress: number): boolean {
        const p = Math.floor(progress);
        const tp = Math.floor(this.progress);
        return (
            this.state === states.PAUSED &&
            Math.abs(p - tp) >= amountOfSecondsToConsiderSeek
        );
    }

    saveUserProgress(): void {
        if (VideoStore.isEditable) return;

        this.lastTimeSavedWatchProgress = new Date();
        if (
            this.watchProgress.start == null ||
            this.watchProgress.end == null
        ) {
            return;
        }

        UserVideoProgressService.saveUserProgress(
            VideoStore.video.uid,
            this.watchProgress.start,
            this.watchProgress.end
        );
    }

    saveStats(state: string) {
        const stat = STATS[state.toUpperCase()];
        let embedder = null;
        if (window.top.location !== window.location) {
            embedder = document.referrer;
        }

        if (!VideoStore.isOwner) {
            VideoStatsService.create(VideoStore.video.rowid, stat, embedder);
        }
    }

    /*
     * Computed
     */

    @computed
    get hasEnded(): boolean {
        return this.progress === VideoStore.video.duration;
    }
}

export default new VideoPlayerStore();



// WEBPACK FOOTER //
// ./src/app/stores/VideoPlayerStore.js